/**
 * Invert the dates of format "3/2021" so that the result can be
 * sorted alphabetically while maintaining the date order. I.e.
 * invert 3/2021 to 2021/3.
 *
 * All other formats result in an empty string.
 *
 * @param {string} date
 */
const sortableDate = (date) => {
  if (typeof date !== "string" || date === "" || date === "/") {
    return "9999/99"; // sort empty dates last
  }
  const parts = date.split("/");
  if (parts.length !== 2) {
    return "";
  }
  return parts[1] + "/" + parts[0];
};

const isMoveReady = (apartment) => {
  return (
    apartment.construction &&
    apartment.construction.ready &&
    apartment.availability.toLowerCase() === "myynnissä"
  );
};

const hasConstructionInfo = (apartment) => {
  return typeof apartment.construction === "object";
};

const sortFloats = (a, b, direction = "asc") => {
  const fa = parseFloat(a);
  const fb = parseFloat(b);
  if (isNaN(fa) && isNaN(fb)) {
    return 0;
  }
  if (isNaN(fa)) {
    return 1;
  }
  if (isNaN(fb)) {
    return -1;
  }
  if (direction === "asc") {
    return fa - fb;
  } else {
    return fb - fa;
  }
};

const sortByUnencumberedPriceAscSingleApartment = (apartmentA, apartmentB) => {
  return sortFloats(
    apartmentA.salesPriceUnencumbered,
    apartmentB.salesPriceUnencumbered,
    "asc"
  );
};

const sortByUnencumberedPriceDescSingleApartment = (apartmentA, apartmentB) => {
  return sortFloats(
    apartmentA.salesPriceUnencumbered,
    apartmentB.salesPriceUnencumbered,
    "desc"
  );
};

const sortByPriceAscHousingCompany = (companyA, companyB) => {
  return sortFloats(
    companyA.aggregations.price.min,
    companyB.aggregations.price.min,
    "asc"
  );
};

const sortByPriceDescHousingCompany = (companyA, companyB) => {
  return sortFloats(
    companyA.aggregations.price.max,
    companyB.aggregations.price.max,
    "desc"
  );
};

const sortBySizeAscSingleApartment = (apartmentA, apartmentB) => {
  return sortFloats(apartmentA.sizeM2, apartmentB.sizeM2, "asc");
};

const sortBySizeDescSingleApartment = (apartmentA, apartmentB) => {
  return sortFloats(apartmentA.sizeM2, apartmentB.sizeM2, "desc");
};

const sortBySizeAscHousingCompany = (companyA, companyB) => {
  return sortFloats(
    companyA.aggregations.size.min,
    companyB.aggregations.size.min,
    "asc"
  );
};

const sortBySizeDescHousingCompany = (companyA, companyB) => {
  return sortFloats(
    companyA.aggregations.size.max,
    companyB.aggregations.size.max,
    "desc"
  );
};

const sortByNewest = (apartmentA, apartmentB) => {
  return (
    new Date(apartmentB.marketingDate) - new Date(apartmentA.marketingDate)
  );
};

const compareConstructionInfoAvailable = (apartmentA, apartmentB) => {
  const hasInfoA = hasConstructionInfo(apartmentA);
  const hasInfoB = hasConstructionInfo(apartmentB);
  if (!hasInfoA && !hasInfoB) return 0;
  if (hasInfoA) return -1;
  if (hasInfoB) return 1;
  return 0;
};

const sortByEstimatedCompletionDate = (apartmentA, apartmentB) => {
  const estimatedCompletionA = sortableDate(
    apartmentA.construction.estimatedCompletion
  );
  const estimatedCompletionB = sortableDate(
    apartmentB.construction.estimatedCompletion
  );

  if (estimatedCompletionA < estimatedCompletionB) {
    return -1;
  } else if (estimatedCompletionB < estimatedCompletionA) {
    return 1;
  }
  return 0;
};

const sortByReadyDate = (apartmentA, apartmentB) => {
  if (isMoveReady(apartmentA) && isMoveReady(apartmentB)) return 0;

  if (isMoveReady(apartmentA)) return -1;
  if (isMoveReady(apartmentB)) return 1;

  if (hasConstructionInfo(apartmentA) && hasConstructionInfo(apartmentB)) {
    return sortByEstimatedCompletionDate(apartmentA, apartmentB);
  } else {
    return compareConstructionInfoAvailable(apartmentA, apartmentB);
  }
};

const sortingMethods = {
  singleApartment: {
    newest: sortByNewest,
    priceAsc: sortByUnencumberedPriceAscSingleApartment,
    priceDesc: sortByUnencumberedPriceDescSingleApartment,
    sizeAsc: sortBySizeAscSingleApartment,
    sizeDesc: sortBySizeDescSingleApartment,
    readyDate: sortByReadyDate,
  },
  housingCompany: {
    newest: sortByNewest,
    priceAsc: sortByPriceAscHousingCompany,
    priceDesc: sortByPriceDescHousingCompany,
    sizeAsc: sortBySizeAscHousingCompany,
    sizeDesc: sortBySizeDescHousingCompany,
    readyDate: sortByReadyDate,
  },
};

const sortingOptions = [
  { value: "newest", label: "Uusimmat ensin" },
  { value: "priceAsc", label: "Edullisimmat ensin" },
  { value: "priceDesc", label: "Kalleimmat ensin" },
  { value: "sizeAsc", label: "Pienimmät ensin" },
  { value: "sizeDesc", label: "Suurimmat ensin" },
  { value: "readyDate", label: "Valmistumisajankohdan mukaan" },
];

export { sortingOptions, sortingMethods };
